import { configOverridesAtom, type SiteConfigOverrides } from "@/config.ts";
import { showError } from "@/modules/error/errorStream.ts";
import { languageStoreAtom } from "@/modules/l10n/i18n.store.ts";
import { allLanguageItems } from "@/modules/l10n/languages.ts";
import { forceDebugAtom } from "@/modules/main/debug/DebugMenu.store.ts";
import { allThemeNames } from "@/modules/theme/allThemeNames.ts";
import { mustGetOwn } from "@warrenio/utils/collections/getOwnProperty";
import { getDefaultStore } from "jotai/vanilla";
import invariant from "tiny-invariant";

/** Read supported URL params, apply overrides, and remove the params from the URL. */
export function applyUrlParamOverrides() {
    if (!import.meta.env.DEV) return;

    try {
        const url = new URL(window.location.href);

        function takeParam(param: string) {
            const value = url.searchParams.get(param);
            url.searchParams.delete(param);
            return value;
        }

        const theme = takeParam("theme");
        const lang = takeParam("lang");
        const debug = takeParam("debug");

        // Remove the query params without causing a navigation event
        history.replaceState(null, "", url);

        const store = getDefaultStore();

        const overrides: SiteConfigOverrides = {};

        if (theme !== null) {
            invariant(allThemeNames.includes(theme), `Unknown theme: "${theme}"`);
            overrides.siteTheme = theme;
        }

        if (lang !== null) {
            invariant(
                allLanguageItems.some((item) => item.id === lang),
                `Unknown language: "${lang}"`,
            );
            store.set(languageStoreAtom, lang);
        }

        if (debug !== null) {
            const value = mustGetOwn({ "": undefined, true: true, false: false }, debug, "forceDebug");
            store.set(forceDebugAtom, value);
        } else if (theme !== null || lang !== null) {
            // Force disable debug menu when we are overriding theme/language
            store.set(forceDebugAtom, false);
        }

        if (Object.keys(overrides).length > 0) {
            store.set(configOverridesAtom, (prev) => ({ ...prev, ...overrides }));
        }
    } catch (e) {
        showError("Failed to apply URL param overrides", e);
    }
}
